import { useAuthStore } from "@/stores/auth.store";

import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

const instance = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
});

// Flag para evitar múltiplos refresh simultâneos
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

instance.interceptors.request.use(
  (config) => {
    const token = useAuthStore().getToken;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Ignorar rotas de autenticação e refresh_token
    const authRoute = "/Auth";
    const isAuthRoute = originalRequest.url?.indexOf(authRoute) !== -1;
    const isRefreshRoute = originalRequest.url?.indexOf("refresh_token") !== -1;
    const isLoginRoute = originalRequest.url?.indexOf("login") !== -1;

    // Se for 401 (token expirado) e NÃO for rota de auth/refresh/login
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthRoute &&
      !isRefreshRoute &&
      !isLoginRoute
    ) {
      // Verificar se é erro de token expirado (StatusCode 4 ou 48)
      const statusCode = (error.response.data as any)?.StatusCode;
      
      if (statusCode === 4 || statusCode === 48) {
        // Verificar se já fez refresh nesta sessão (permitir apenas 1 refresh)
        const hasRefreshed = useAuthStore().getHasRefreshed;
        
        if (hasRefreshed) {
          // Já fez refresh antes, forçar logout após 4 horas
          useAuthStore().setLogout();
          return Promise.reject(error);
        }
        
        // Token expirado - tentar refresh (primeira vez)
        if (isRefreshing) {
          // Já está refreshing, adicionar à fila
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(() => {
              // Retentar com novo token
              return instance(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const refreshToken = useAuthStore().getRefreshToken;

          if (!refreshToken) {
            console.error("[Axios Interceptor] Sem refresh_token, fazendo logout");
            useAuthStore().setLogout();
            return Promise.reject(error);
          }

          // Tentar refresh (POST /api/refresh_token)
          const response = await instance.post("refresh_token", {
            refresh_token: refreshToken,
          });

          if (response.data?.data?.access_token) {
            const newAccessToken = response.data.data.access_token;
            const newRefreshToken = response.data.data.refresh_token;

            // Atualizar tokens no store
            useAuthStore().setToken(newAccessToken);
            if (newRefreshToken) {
              useAuthStore().setRefreshToken(newRefreshToken);
            }

            // Atualizar expiresAt (4 horas)
            const newExpiresAt = Date.now() + 4 * 60 * 60 * 1000;
            useAuthStore().setExpiresAt(newExpiresAt);
            
            // Marcar que já fez refresh (próxima expiração forçará logout)
            useAuthStore().setHasRefreshed(true);

            console.log("[Axios Interceptor] Token renovado - sessão expirará em 4 horas");

            // Processar fila de requisições pendentes
            processQueue(null, newAccessToken);

            // Retentar requisição original com novo token
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            }
            return instance(originalRequest);
          } else {
            throw new Error("Refresh response inválido");
          }
        } catch (refreshError) {
          console.error("[Axios Interceptor] Falha no refresh, fazendo logout", refreshError);
          processQueue(refreshError as AxiosError, null);
          useAuthStore().setLogout();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else {
        // Outro tipo de 401 (credenciais inválidas, etc.) - fazer logout
        console.error("[Axios Interceptor] 401 não relacionado a token expirado");
        useAuthStore().setLogout();
      }
    }

    return Promise.reject(error);
  }
);

export default instance;
